import isEmail from 'validator/es/lib/isEmail';
import isDecimal from 'validator/es/lib/isDecimal';
import isURL from 'validator/es/lib/isURL';
import isInt from 'validator/es/lib/isInt';
import isAlphanumeric from 'validator/es/lib/isAlphanumeric';
import { RegisterOptions } from 'react-hook-form';
import { PromocodePattern } from '../export-types/cleaned-types';
import { DECIMAL_PLACES } from './common.utils';
import { useIntl } from 'react-intl';

type Validator = Omit<
  RegisterOptions,
  'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled' | 'deps'
>;

export const required: Validator = {
  required: 'Заполните поле',
};
export const articleStoreValidation: Validator = {
  validate: {
    correctArticle: (v) =>
      /^[A-Za-z0-9_]+-\d+$/.test(v) ||
      'Артикул должен быть в формате "NB573-1"',
  },
};
export function useRequired(): Validator {
  const intl = useIntl();
  const requiredMessage = intl.formatMessage({
    defaultMessage: 'Заполните поле',
    id: 'Validations.title.FillInTheField',
    description: 'Сообщение',
  });

  return {
    required: requiredMessage,
  };
}

export function useDecimalValidation(): Validator {
  const intl = useIntl();
  const message = intl.formatMessage({
    defaultMessage: 'Введите число больше 0',
    id: 'Validations.title.EnterNumberGreaterThan0',
    description: 'Сообщение',
  });

  return {
    validate: {
      correctDecimal: (v) => {
        const DECIMAL_PLACES = 2; // Укажите количество десятичных разрядов
        if (
          (typeof v === 'string' && v.length === 0) ||
          (v !== '0' &&
            isDecimal(`${v}`, { decimal_digits: `0,${DECIMAL_PLACES}` }))
        ) {
          return true;
        } else {
          return message;
        }
      },
    },
  };
}

export const phoneValidation: Validator = {
  validate: {
    length: (v) =>
      v.match(/\d+/g).join('').length === 11 ||
      'Номер телефона должен состоять из 10 цифр, начиная с кода оператора',
  },
};

export const emailValidation: Validator = {
  validate: {
    correctEmail: (v) =>
      (typeof v === 'string' && v.length === 0) ||
      isEmail(`${v}`) ||
      'Введите корректный e-mail',
  },
};

export const cardValidation: Validator = {
  validate: {
    correctCard: (v) =>
      (typeof v === 'string' && v.length === 0) ||
      true ||
      'Введите корректный номер карты',
  },
};

export const decimalValidation: Validator = {
  validate: {
    correctDecimal: (v) =>
      (typeof v === 'string' && v.length === 0) ||
      isDecimal(`${v}`, { decimal_digits: '0,2' }) ||
      'Введите число',
  },
};
export const fiveDecimalPlacesValidation: Validator = {
  validate: {
    correctDecimal: (v) =>
      (typeof v === 'string' && v.length === 0) ||
      isDecimal(`${v}`, { decimal_digits: '0,5' }) ||
      'Введите число',
  },
};

export const decimalValidationCurrency: Validator = {
  validate: {
    correctDecimal: (v) =>
      (typeof v === 'string' && v.length === 0) ||
      isDecimal(`${v}`, { decimal_digits: `0,${DECIMAL_PLACES}` }) ||
      'Введите число',
  },
};

export const intValidation: Validator = {
  validate: {
    correctInt: (v) =>
      (typeof v === 'string' && v.length === 0) ||
      isInt(`${v}`) ||
      'Введите целое число',
  },
};

export const urlValidation: Validator = {
  validate: {
    correctUrl: (v) =>
      (typeof v === 'string' && v.length === 0) ||
      isURL(`${v}`, { require_protocol: true }) ||
      'Введите ссылку',
  },
};

export const alphanumericValidation: Validator = {
  validate: {
    correctAlphanumeric: (v) =>
      (typeof v === 'string' && v.length === 0) ||
      isAlphanumeric(`${v}`) ||
      'Введите строку состоящую только из чисел и латинских букв',
  },
};

export const promocodeValidation: Validator = {
  validate: {
    withoutWhitespace: (v) =>
      PromocodePattern.test(v) || 'Строка не должна содержать пробелов',
  },
};

export const decimalValidationAdditionalServiseSum: Validator = {
  validate: {
    correctDecimal: (v) =>
      (typeof v === 'string' && v.length === 0) ||
      (v !== '0' &&
        isDecimal(`${v}`, { decimal_digits: `0,${DECIMAL_PLACES}` })) ||
      'Введите число больше 0',
  },
};

export const nonNegativeNumberValidation: Validator = {
  validate: {
    nonNegativeDecimal: (v) =>
      (typeof v === 'string' && parseFloat(v as string) >= 0) ||
      'Введите число больше или равное нулю',
  },
};

export const passportValidation = {
  validate: {
    correctPassport: (v: string | undefined) =>
      (typeof v === 'string' && v.length === 0) ||
      v?.replace(/[^\d]/g, '').length === 10 ||
      'Укажите серию и номер паспорта',
  },
};

export const innValidation = {
  validate: {
    correctInn: (v: string) =>
      v !== '' || isInt(`${v}`) || 'Укажите ИНН организации или ИП',
    length: (v: string) =>
      v.length === 10 ||
      v.length === 12 ||
      'ИНН должен состоять из 10 или 12 цифр',
  },
};

export const ogrnValidation = {
  validate: {
    correctOgrn: (v?: string) =>
      !v || v !== '' || isInt(`${v}`) || 'Укажите ОРГН / ОГРНИП',
    length: (v?: string) =>
      !v ||
      v?.length === 13 ||
      v?.length === 15 ||
      'ОРГН / ОГРНИП должен состоять из 13 или 15 цифр',
  },
};

export const bicValidation = {
  validate: {
    correctBic: (v: string) => v !== '' || isInt(`${v}`) || 'Укажите БИК',
    length: (v: string) => v.length === 9 || 'Бик должен состоять из 9 цифр',
  },
};

export const accountValidation = {
  validate: {
    correctAccount: (v: string) =>
      v !== '' && isInt(v) ? true : 'Счет должен состоять только из цифр',
  },
};

export const kppValidation = {
  validate: {
    correctKpp: (v?: string) =>
      !v || v !== '' || isInt(`${v}`) || 'Укажите КПП',
    length: (v?: string) =>
      !v || v.length === 9 || 'КПП должен состоять из 9 цифр',
  },
};

export const nonNegativeIntValidation: Validator = {
  validate: {
    correctNonNegativeInt: (v) =>
      (typeof v === 'string' && v.length === 0) ||
      (isInt(`${v}`) && parseInt(v) >= 0) ||
      'Введите целое неотрицательное число',
  },
};
